import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Typography } from '@mui/joy';
import { Stepper, Step, StepLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Text, Input } from 'src/mtska-frontend-app-component';
import Rules from '../../../../mtska-frontend-app-component/components/Segment/Rules';
import SegmentationSummary from '../../../../mtska-frontend-app-component/components/Segment/SegmentationSummary';
import SegmentConfirmation from '../../../../mtska-frontend-app-component/components/Segment/SegmentConfirmation';
import List from '../components/ListLead';
import dayjs from 'dayjs';
import { useDataProvider } from "../../../../mtska-frontend-data-provider";
import JSONPretty from "react-json-pretty";

const AudienceSegmentCreator = ({
    externalSetForwardDisabled = () => { },
    externalSetBackDisabled = () => { },
    externalSetActionInvoked = () => { },
    externalSetActionLabel = () => { },
    externalSetErrorMessage = () => { },
    actionInvoked,
    onSave = () => { },
    item
}) => {
    
    const { t } = useTranslation();
    const steps = [t('Info'), t('Rules'), t('Summary'), t('Confirm')];
    const [activeStep, setActiveStep] = useState(0);
    const [mappingValue, setMappingValue] = useState(item?.segmentationRules ?? []);
    const [totalLeads, setTotalLeads] = useState(0);
    const isSaving = useRef(false);
    const [filters, setFilters] = useState({
        items: (item?.segmentationRules ?? []).map(rule => ({
            field: rule.field,
            operator: rule.operator,
            value: rule.value
        }))
    });
    const [isCompleted, setIsCompleted] = useState(false);

    const { control, setValue, watch } = useForm({
        mode: 'onChange',
        defaultValues: {
            segmentName: '',
            description: '',
            segmentColor: '#E9B712',
            creationDate: dayjs().format('YYYY-MM-DD')
        }
    });

    const { item: config } = useDataProvider('configs/config');
    const moduleConfig = config?.data?.modules?.['audiences/audiencelead'];
    const importModel = moduleConfig?.importModel || [];
    const modelFields = Array.isArray(importModel)
        ? importModel.map(item => ({ key: item.key, label: t(item.label) }))
        : Object.values(importModel)
            .filter(item => item && item.key && item.label)
            .map(item => ({ key: item.key, label: t(item.label) }));

    const [validationState, setValidationState] = useState({});

    useEffect(() => {
        if (item) {
            setValue('segmentName', item.name || '');
            setValue('description', item.description || '');
            setValue('segmentColor', item.color || '#E9B712');
            setValue('creationDate', item.creation_date || dayjs().format('YYYY-MM-DD'));

            if (item.segmentationRules) {
                const rules = item.segmentationRules.map(rule => ({
                    field: rule.field || '',
                    operator: rule.operator || '=',
                    value: rule.value || ''
                }));
                setMappingValue(rules);
                setFilters({ items: rules });
                setValue("rules", rules);
            }
        }
    }, [item, setValue]);



    useEffect(() => {
        externalSetBackDisabled(activeStep === 0 || isCompleted);
        externalSetForwardDisabled(isCompleted);
        externalSetActionLabel(activeStep === steps.length - 2 ? t('Submit') : (isCompleted ? t('Close') : t('Next')));
    }, [activeStep, steps.length, t, externalSetBackDisabled, externalSetForwardDisabled, externalSetActionLabel, isCompleted]);

    useEffect(() => {
        if (typeof actionInvoked === 'undefined' || actionInvoked === 0 || isCompleted) return;

        const currentStep = activeStep;
        const totalSteps = steps.length;

        if (actionInvoked > 0) {
            let stepError = null;
            if (currentStep === 0) {
                const segmentNameValue = watch('segmentName');
                if (!segmentNameValue || segmentNameValue.trim() === '') {
                    stepError = t('MandatoryName');
                }
            }

            if (stepError) {
                setValidationState(prev => ({ ...prev, [currentStep]: stepError }));
                externalSetErrorMessage(stepError);
                externalSetActionInvoked(0);
                return;
            } else {
                setValidationState(prev => ({ ...prev, [currentStep]: null }));
                externalSetErrorMessage(null);
            }

            if (currentStep === totalSteps - 2) {
                if (isSaving.current) return;
                isSaving.current = true;
                const formData = {
                    id: item?.id,
                    name: watch('segmentName'),
                    description: watch('description'),
                    color: watch('segmentColor'),
                    creation_date: watch('creationDate'),
                    segmentationRules: mappingValue,
                    status: 'draft'
                };
                if (onSave) {
                    onSave(formData)
                        .then(() => {
                            setActiveStep(currentStep + 1);
                            setIsCompleted(true);
                            externalSetActionInvoked(0);
                            isSaving.current = false;
                        })
                        .catch(error => {
                            externalSetErrorMessage(error.message || t('failedSavingSegment'));
                            externalSetActionInvoked(0);
                            isSaving.current = false;
                        });
                } else {
                    isSaving.current = false;
                    setActiveStep(currentStep + 1);
                    setIsCompleted(true);
                    externalSetActionInvoked(0);
                }
            } else {
                setActiveStep(currentStep + 1);
                externalSetActionInvoked(0);
            }
        } else if (actionInvoked < 0) {
            if (currentStep > 0) {
                setActiveStep(currentStep - 1);
                externalSetErrorMessage(null);
            }
            externalSetActionInvoked(0);
        }
    }, [actionInvoked, activeStep, steps.length, externalSetActionInvoked, externalSetErrorMessage, watch, mappingValue, onSave, t, isCompleted]);

    const summaryFields = [
        { label: 'Nome segmento', value: watch('segmentName') || 'N/A' },
        { label: 'Descrizione', value: watch('description') || 'N/A' },
        { label: 'Colore', value: watch('segmentColor') || 'N/A' },
        { label: 'Data creazione', value: watch('creationDate') || 'N/A' },
        { label: 'Totale lead a oggi', value: totalLeads },
    ];

    const handleTotalChange = useCallback((newTotal) => {
        setTotalLeads(newTotal);
    }, []);

    const handleRulesChange = useCallback((newRules) => {
        setMappingValue(newRules);
        setFilters({
            items: newRules.map(rule => ({
                field: rule.field,
                operator: rule.operator,
                value: rule.value
            }))
        });
    }, []);

    return (
        <Box className="audiencesegmentcreator">
            {/* <JSONPretty data={item} className='json-wrapper'></JSONPretty> */}
            <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 1 }}>
                {steps.map((label, index) => {
                    const labelProps = {};
                    const stepError = validationState[index];
                    if (stepError) {
                        labelProps.optional = (
                            <Typography variant="caption" color="error">
                                {stepError}
                            </Typography>
                        );
                        labelProps.error = true;
                    }
                    return (
                        <Step key={label}>
                            <StepLabel {...labelProps}>{t(label)}</StepLabel>
                        </Step>
                    );
                })}
            </Stepper>
            <Box className="form-wrapper">
                <Box sx={{ display: activeStep === 0 ? 'block' : 'none' }}>
                    <fieldset className="mtska-fieldset" style={{ border: 'none' }}>
                        <Text level="title-md" className="mtska-fieldset-title">{t('infoSegment')}</Text>
                        <Box className="form-element text col-12">
                            <Input label={t('segmentName')} type="text" name="segmentName" control={control} editing={true} required={true} />
                        </Box>
                        <Box className="form-element text col-12">
                            <Input label={t('segmentDescription')} type="textarea" name="description" control={control} editing={true} rows={3} />
                        </Box>
                        <Box className="form-element text col-6">
                            <Input label={t('segmentColor')} type="colorpicker" name="segmentColor" control={control} editing={true} />
                        </Box>
                        <Box className="form-element text col-6">
                            <Input label={t('creationDate')} type="date" name="creationDate" control={control} editing={false} />
                        </Box>
                    </fieldset>
                </Box>
                <Box sx={{ display: activeStep === 1 ? 'block' : 'none', px: 2 }}>
                    <div className="form-element col-12" style={{ marginBottom: '32px' }}>
                        <Rules model={modelFields} control={control} defaultRules={mappingValue} onRulesChange={handleRulesChange} setValue={setValue} />
                    </div>
                    <List onTotalChange={handleTotalChange} filters={filters} />
                </Box>
                <Box sx={{ display: activeStep === 2 ? 'block' : 'none', px: 2 }}>
                    <SegmentationSummary summaryFields={summaryFields} mappingValue={mappingValue} modelFields={modelFields} isSaving={isSaving} />
                </Box>
                <SegmentConfirmation isCompleted={isCompleted} />
            </Box>
        </Box>
    );
};

export default AudienceSegmentCreator;

